// components/CareHomeSelectionModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { CareHome } from '../types/customer';
import { CustomerAddress } from '../types/address';

// Type matching the C# CareHomeSearchResultDto
interface CareHomeSearchResult {
  externalId: string; // Guid from C#
  name: string;
  contactPerson?: string | null;
  contactEmail?: string | null;
  primaryAddress?: CustomerAddress | null;
}

interface CareHomeWithDetails extends CareHome {
  addresses?: CustomerAddress[];
  postcode?: string; // For search/display purposes
  contactPerson?: string | null;
  contactEmail?: string | null;
  primaryAddress?: CustomerAddress | null;
}

interface FormattedCareHome extends CareHomeWithDetails {
  displayName: string;
  displayAddress: string;
}

interface CareHomeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectCareHome: (careHome: CareHomeWithDetails) => void;
  onSearchCareHomes: (searchTerm: string) => Promise<CareHomeSearchResult[]>;
  loading?: boolean;
}

export const CareHomeSelectionModal: React.FC<CareHomeSelectionModalProps> = ({
  open,
  onClose,
  onSelectCareHome,
  onSearchCareHomes,
  loading = false,
}) => {
  const MAX_SEARCH_RESULTS = 100;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [careHomes, setCareHomes] = useState<CareHomeSearchResult[]>([]);
  const [selectedCareHome, setSelectedCareHome] = useState<FormattedCareHome | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced search function
  const searchCareHomes = useCallback(async (term: string) => {
    if (term.length < 3) {
      setCareHomes([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await onSearchCareHomes(term);
      setCareHomes(results);
      setHasSearched(true);
    } catch (error) {
      console.error('Error searching care homes:', error);
      setCareHomes([]);
    } finally {
      setIsSearching(false);
    }
  }, [onSearchCareHomes]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCareHomes(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCareHomes]);

  const handleClose = () => {
    setSearchTerm('');
    setCareHomes([]);
    setSelectedCareHome(null);
    setHasSearched(false);
    onClose();
  };

  const handleSelect = () => {
    if (selectedCareHome) {
      onSelectCareHome(selectedCareHome);
      handleClose();
    }
  };

  const formatCareHomeOption = (careHome: CareHomeSearchResult): FormattedCareHome => {
    // Use the primary address from the search result
    const primaryAddress = careHome.primaryAddress;
    
    const address = primaryAddress ? [
      primaryAddress.street,
      primaryAddress.nameNumber,
      primaryAddress.postcode,
      primaryAddress.town,
    ].filter(Boolean).join(', ') : 'No address available';

    return {
      externalId: careHome.externalId,
      name: careHome.name,
      contactPerson: careHome.contactPerson,
      contactEmail: careHome.contactEmail,
      primaryAddress: careHome.primaryAddress,
      displayName: careHome.name,
      displayAddress: address,
    };
  };

  const formattedCareHomes: FormattedCareHome[] = careHomes.map(formatCareHomeOption);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon />
          <Typography variant="h6">Select Care Home</Typography>          
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for a care home by name, postcode, or contact details to add its address to the shared address.
          </Typography>
          {careHomes.length >= MAX_SEARCH_RESULTS && hasSearched && (
            <Box 
              sx={{ 
                p: 2, 
                mb: 2,
                border: 1, 
                borderColor: 'warning.main',
                borderRadius: 1,
                backgroundColor: 'warning.50',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <SearchIcon color="warning" />
              <Typography variant="body2" color="warning.dark">
                Showing first {MAX_SEARCH_RESULTS} results. For better results, try a more specific search term.
              </Typography>
            </Box>
          )}
          <Autocomplete
            options={formattedCareHomes}
            value={selectedCareHome}
            onChange={(_, newValue) => setSelectedCareHome(newValue)}
            inputValue={searchTerm}
            onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
            loading={isSearching}
            filterOptions={(options) => options} // Disable built-in filtering           
            loadingText="Searching care homes..."
            noOptionsText={
              !hasSearched 
                ? "Start typing to search care homes (min 3 characters)..."
                : searchTerm.length < 3
                ? "Enter at least 3 characters to search"
                : "No care homes found"
            }
            getOptionLabel={(option) => option.displayName || ''}
            isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Care Homes"
                placeholder="Type care home name or postcode..."
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.externalId}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <HomeIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                      {option.displayName}
                    </Typography>
                    {option.externalId && (
                      <Chip 
                        label={option.externalId} 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </Box>
                  {option.displayAddress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {option.displayAddress}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            sx={{ mb: 2 }}
          />

         

          {selectedCareHome && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selected Care Home:
                </Typography>
                <Box 
                  sx={{ 
                    p: 2, 
                    border: 1, 
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    backgroundColor: 'primary.50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <HomeIcon color="primary" />
                    <Typography variant="h6" color="primary">
                      {selectedCareHome.displayName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOnIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedCareHome.displayAddress}
                    </Typography>
                  </Box>
                  {selectedCareHome.contactPerson && (
                    <Typography variant="body2" color="text.secondary">
                      Contact: {selectedCareHome.contactPerson}
                    </Typography>
                  )}
                  {selectedCareHome.contactEmail && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {selectedCareHome.contactEmail}
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSelect} 
          variant="contained"
          disabled={!selectedCareHome || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <HomeIcon />}
        >
          {loading ? 'Adding...' : 'Add Care Home Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};